import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

/**
 * Handwritten signature pad (K2c) — no external library.
 *
 * Pointer Events cover mouse + touch + pen uniformly (iPad/iPhone Safari,
 * Android Chrome, desktop). Imperative API via ref:
 *   - clear()      reset the surface
 *   - isEmpty()    has anything been drawn?
 *   - toDataUrl()  PNG data URL ("data:image/png;base64,...") or null if empty
 *
 * Coordinate correctness (the classic mobile off-by-finger bug) is handled by:
 *   1. DPR scaling   — bitmap = CSS size × devicePixelRatio, ctx.scale(dpr,dpr)
 *   2. getBoundingClientRect for pointer coords (NOT e.offsetX/offsetY, which is
 *      unreliable on Safari/touch)
 *   3. touch-action: none on the canvas so the browser draws instead of
 *      scrolling/zooming
 *   4. resize / orientationchange re-sizes the bitmap (content preserved via a
 *      snapshot) so the surface never stretches
 */
const SignaturePad = forwardRef(function SignaturePad(
  { className = "", height = 200, penColor = "#0f172a", onEmptyChange },
  ref
) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const activePointerRef = useRef(null);

  // Configure the 2D context for the current CSS size + devicePixelRatio.
  const setupContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return; // jsdom / unsupported — stay inert
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const cssW = Math.max(1, Math.round(rect.width));
    const cssH = Math.max(1, Math.round(rect.height));
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset any prior scale before re-scaling
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = penColor;
    ctxRef.current = ctx;
  }, [penColor]);

  // Re-size on viewport/orientation change, preserving the current drawing so a
  // half-finished signature is not lost (snapshot → resize → redraw scaled).
  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let snapshot = null;
    if (hasDrawnRef.current) {
      try {
        snapshot = canvas.toDataURL("image/png");
      } catch (_) {
        snapshot = null;
      }
    }
    setupContext();
    if (snapshot) {
      const img = new Image();
      img.onload = () => {
        const ctx = ctxRef.current;
        if (!ctx || !canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = snapshot;
    }
  }, [setupContext]);

  useEffect(() => {
    setupContext();
    const onResize = () => handleResize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [setupContext, handleResize]);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    e.preventDefault();
    activePointerRef.current = e.pointerId;
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch (_) {
      /* capture unsupported — pointerup still ends the stroke */
    }
    drawingRef.current = true;
    const { x, y } = pointerPos(e);
    lastRef.current = { x, y };
    // Render a dot so a single tap leaves a visible mark.
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.01, y + 0.01);
    ctx.stroke();
    if (!hasDrawnRef.current) {
      hasDrawnRef.current = true;
      onEmptyChange?.(false);
    }
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    if (
      activePointerRef.current !== null &&
      e.pointerId !== activePointerRef.current
    ) {
      return;
    }
    const ctx = ctxRef.current;
    if (!ctx) return;
    e.preventDefault();
    const { x, y } = pointerPos(e);
    const last = lastRef.current;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = { x, y };
  };

  const endStroke = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas && e && e.pointerId != null) {
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }
    }
    activePointerRef.current = null;
  };

  useImperativeHandle(
    ref,
    () => ({
      clear() {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (canvas && ctx) {
          const rect = canvas.getBoundingClientRect();
          ctx.clearRect(0, 0, rect.width, rect.height);
        }
        if (hasDrawnRef.current) {
          hasDrawnRef.current = false;
          onEmptyChange?.(true);
        }
      },
      isEmpty() {
        return !hasDrawnRef.current;
      },
      toDataUrl() {
        const canvas = canvasRef.current;
        if (!canvas || !hasDrawnRef.current) return null;
        try {
          return canvas.toDataURL("image/png");
        } catch (_) {
          return null;
        }
      },
    }),
    [onEmptyChange]
  );

  return (
    <canvas
      ref={canvasRef}
      className={`w-full touch-none rounded-[6px] border border-black/20 bg-white dark:border-white/20 ${className}`}
      style={{ height: `${height}px`, touchAction: "none", cursor: "crosshair" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endStroke}
      onPointerCancel={endStroke}
    />
  );
});

export default SignaturePad;
